import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AccordionComponent, ButtonComponent, DropdownSelectComponent, UploadComponent } from '@ppl/ui';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule as PrimeButtonModule } from 'primeng/button';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TimesheetService } from '../../../../../services/timesheet.service';
import { UserService } from '../../../../../services/user.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { HttpRequestService } from '../../../../../shared/services/http-request.service';
import { ENDPOINTS } from '../../../../../shared/constants/url.constants';
import { formatUrl } from '../../../../../shared/utils/format-url-util';
import { LookupService } from '../../../../../services/lookup.service';
import { LookupValueItem } from '../../../../../interfaces/lookup.interface';

// ── Constants ────────────────────────────────────────────────────────────────

const BATCH_TYPE_ADJUST           = 'A';
const BATCH_TYPE_AUTH_ADJUSTMENT  = 'AA';
const BATCH_PROCESS_PURCHASE      = 'P';
const BATCH_PROCESS_INVOICE       = 'I';
const BATCH_PROCESS_TIMESHEET     = 'T';
const NAVIGATE_BACK_ROUTE         = '/home/bulkAdjustments';

const LOOKUP_KEYS = {
  BATCH_TYPE:        'Mass Adjustment Type',
  CRITERIA_COLUMNS:  'Mass Adjustment Critieria',
  ADJUSTMENT_COLUMNS:'Mass Adjustment Fields',
  PROCESS_TYPE:      'Mass Adjustment Process Type',
  BATCH_REASON:      'adjustment_reason',
} as const;

const OPERATOR_OPTIONS: ReadonlyArray<OperatorOption> = [
  { label: 'Contains',              value: 'CONTAINS' },
  { label: 'Starts with',           value: 'STARTS WITH' },
  { label: 'Ends with',             value: 'ENDS WITH' },
  { label: 'Greater than',          value: 'GREATER THAN' },
  { label: 'Greater than Or Equal', value: 'GREATER THAN EQUAL TO' },
  { label: 'Equal',                 value: 'EQUAL' },
  { label: 'Less than',             value: 'LESS THAN' },
  { label: 'Less than Or Equal',    value: 'LESS THAN EQUAL TO' },
  { label: 'Not Equal',             value: 'NOT EQUAL' },
  { label: 'Like',                  value: 'LIKE' },
  { label: 'In',                  value: 'IN' },
];

// ── Types ────────────────────────────────────────────────────────────────────

type InputMode = 'upload' | 'criteria' | null;
type OperatorOption = Readonly<{ label: string; value: string }>;

interface RowItem {
  columnLkp:   string;
  operatorLkp: string;
  value:       string;
}

interface ServiceCodeOption {
  service_code:        string;
  service_description: string;
}

interface BatchPayload {
  batchInstanceId: string;
  batchTypeLkp:    string;
  batchProcessLkp: string;
  reasonCodeLkp:   string;
  programCode:     string;
  isActive:        boolean;
  criteriaList:    RowItem[];
  fileDetails: {
    fileUploadDetailId: number | string | null;
  };
}

export interface AuthOption {
  tas_authorization_detail_id: number;
  ppl_auth_number:             string;
  plan_servicecode:            string;
  service_code:                string;
  service_description:         string;
  auth_from_date:              string;
  auth_to_date:                string;
  total_used_units?:           number;
  total_units?:                number;
  used_units?:                 number;
  available_units?:            number;
  org_name?:                   string;
}

// ── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'ppl-add-mass-adjustment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgZorroModule,
    ButtonComponent,
    AccordionComponent,
    InputTextModule,
    PrimeButtonModule,
    DropdownSelectComponent,
    UploadComponent,
  ],
  templateUrl: './add-mass-adjustment.component.html',
  styleUrl: './add-mass-adjustment.component.css',
})
export class AddMassAdjustmentComponent implements OnInit {

  // ── Form ──────────────────────────────────────────────────────────────────

  readonly adjustmentForm = this.fb.group({
    batchInstanceId: [''],
    batchTypeLkp:    ['', [Validators.required]],
    batchProcessLkp: ['', Validators.required],
    reasonCodeLkp:   ['', Validators.required],
    programCode:     [{ value: '', disabled: true }],
    criteria:        this.fb.array<FormGroup>([]),
    isActive:        [true],
    // Auth Adjustment fields — only used when batchTypeLkp === 'AA'
    authParticipantId: [''],
    authFromDate:      [null as Date | null],
    authToDate:        [null as Date | null],
    oldServiceCode:    [''],
    newServiceCode:    [''],
    oldAuthId:         [null as number | null],
    newAuthId:         [null as number | null],
  });

  // ── Standard-mode state ───────────────────────────────────────────────────

  isSaving          = false;
  isUploadingFile   = false;
  inputMode: InputMode = null;
  selectedBatchType = '';
  uploadedFiles: File[] = [];
  fileUploadDetailId: number | string | null = null;
  fileList: any[] = [];

  // ── Auth Adjustment state ─────────────────────────────────────────────────

  oldServiceOptions: ServiceCodeOption[] = [];
  newServiceOptions: ServiceCodeOption[] = [];
  oldAuthOptions: AuthOption[] = [];
  newAuthOptions: AuthOption[] = [];
  private usedAuthOptions: AuthOption[] = [];
  private activeAuthOptions: AuthOption[] = [];
  isLoadingOldServices = false;
  isLoadingNewServices = false;
  isLoadingNewAuths = false;

  // ── Edit mode ─────────────────────────────────────────────────────────────

  isEditMode = false;
  private editBatchInstanceId: string | null = null;
  private programCode = '';

  // ── Lookup data ───────────────────────────────────────────────────────────

  batchTypeOptions:       LookupValueItem[] = [];
  batchReasonOptions:     LookupValueItem[] = [];
  columnOptions:          LookupValueItem[] = [];
  adjustmentColumnOptions:LookupValueItem[] = [];
  processType:            LookupValueItem[] = [];
  readonly operatorOptions = OPERATOR_OPTIONS;

  // ── Convenience getters ───────────────────────────────────────────────────

  get criteriaFormArray(): FormArray<FormGroup> {
    return this.adjustmentForm.get('criteria') as FormArray<FormGroup>;
  }

  get isAuthAdjustmentMode(): boolean {
    return this.adjustmentForm.get('batchTypeLkp')?.value === BATCH_TYPE_AUTH_ADJUSTMENT;
  }

  get canSubmit(): boolean {
    if (!this.adjustmentForm.valid) return false;

    if (this.isAuthAdjustmentMode) {
      const f = this.adjustmentForm.getRawValue();
      return !!(f.authParticipantId && f.authFromDate && f.authToDate &&
        f.oldServiceCode && f.newServiceCode && f.oldAuthId && f.newAuthId &&
        f.oldAuthId !== f.newAuthId);
    }

    return (
      (this.criteriaFormArray.length > 0 &&
        this.criteriaFormArray.controls.some(
          row => row.get('columnLkp')?.value &&
                 row.get('operatorLkp')?.value &&
                 row.get('value')?.value,
        )) ||
      this.uploadedFiles.length > 0 ||
      !!this.fileUploadDetailId
    );
  }

  // ── Constructor & lifecycle ───────────────────────────────────────────────

  private readonly navigationState: Record<string, any> | undefined;

  constructor(
    private readonly fb:                   FormBuilder,
    private readonly router:               Router,
    private readonly userService:          UserService,
    private readonly timesheetService:     TimesheetService,
    private readonly notificationService:  NotificationService,
    private readonly http:                 HttpRequestService,
    private readonly lookupService:        LookupService,
  ) {
    this.navigationState = this.router.getCurrentNavigation()?.extras?.state;
  }

  ngOnInit(): void {
    this.initProgramContext();
    this.initLookups();
    this.initEditState();
  }

  // ── Batch dropdown handlers ───────────────────────────────────────────────

  onBatchProcessChange(value: string): void {
    const allBatchTypes = this.lookupService.getLookupValue(LOOKUP_KEYS.BATCH_TYPE);
    if (value === BATCH_PROCESS_PURCHASE) {
      this.batchTypeOptions = allBatchTypes.filter(o => o.lkpValueCode === 'V');
    } else if (value === BATCH_PROCESS_INVOICE) {
      // Invoice supports Auth Adjustment, but not the standard Adjust type.
      this.batchTypeOptions = allBatchTypes.filter(
        o => o.lkpValueCode !== BATCH_TYPE_ADJUST,
      );
    } else {
      this.batchTypeOptions = allBatchTypes;
    }

    const currentBatchType = this.adjustmentForm.get('batchTypeLkp')?.value;
    if (currentBatchType && !this.batchTypeOptions.some(o => o.lkpValueCode === currentBatchType)) {
      this.adjustmentForm.get('batchTypeLkp')?.setValue('');
      this.selectedBatchType = '';
      this.clearAuthAdjustmentState();
    }

    if (this.isAuthAdjustmentMode) {
      this.onAuthCriteriaChange();
    }
  }

  onBatchTypeChange(value: string): void {
    this.selectedBatchType = value;
    if (value !== BATCH_TYPE_AUTH_ADJUSTMENT) {
      this.clearAuthAdjustmentState();
    } else {
      // Switching to AA — clear standard criteria / upload
      this.criteriaFormArray.clear();
      this.inputMode          = null;
      this.uploadedFiles      = [];
      this.fileUploadDetailId = null;
      this.fileList           = [];
    }
  }

  // ── Input mode (criteria vs upload) ───────────────────────────────────────

  selectCriteriaMode(): void {
    if (this.inputMode === 'upload') return;
    this.inputMode = 'criteria';
    if (this.criteriaFormArray.length === 0) {
      this.addRow(this.criteriaFormArray);
    }
  }

  // ── Generic row helpers ────────────────────────────────────────────────────

  addRow(formArray: FormArray<FormGroup>, insertAfter?: number): void {
    const group = this.createRowGroup();
    if (typeof insertAfter === 'number' && insertAfter >= 0) {
      formArray.insert(insertAfter + 1, group);
    } else {
      formArray.push(group);
    }
  }

  removeRow(formArray: FormArray<FormGroup>, index: number): void {
    formArray.removeAt(index);
  }

  addCriteriaRow(index?: number): void {
    this.addRow(this.criteriaFormArray, index);
    this.inputMode = 'criteria';
  }

  removeCriteriaRow(index: number): void {
    this.removeRow(this.criteriaFormArray, index);
    if (this.criteriaFormArray.length === 0) {
      this.inputMode = null;
    }
  }

  // ── Auth Adjustment picker ─────────────────────────────────────────────────

  loadOldAuths(): void {
    const f = this.adjustmentForm.getRawValue();
    if (!f.batchProcessLkp || !f.authParticipantId || !f.authFromDate || !f.authToDate) {
      this.notificationService.error('Error',
        'Please select Batch Source and enter Participant ID, From Date and To Date first.');
      return;
    }
    const url = `${formatUrl(ENDPOINTS.MASS_ADJUSTMENT.USED_AUTHS, { programCode: this.programCode })}` +
                `?participantId=${encodeURIComponent(String(f.authParticipantId))}` +
                `&fromDate=${this.formatDate(f.authFromDate as Date)}` +
                `&toDate=${this.formatDate(f.authToDate as Date)}` +
                `&batchProcessLkp=${encodeURIComponent(String(f.batchProcessLkp))}`;

    this.isLoadingOldServices = true;
    this.usedAuthOptions      = [];
    this.oldServiceOptions    = [];
    this.oldAuthOptions       = [];
    this.newServiceOptions    = [];
    this.activeAuthOptions    = [];
    this.newAuthOptions       = [];
    this.adjustmentForm.patchValue({
      oldServiceCode: '',
      newServiceCode: '',
      oldAuthId:      null,
      newAuthId:      null,
    });
    this.http.get(url)
      .pipe(finalize(() => { this.isLoadingOldServices = false; }))
      .subscribe({
        next: (res: any) => {
          const data = res?.responsedata ?? res?.data ?? res ?? [];
          this.usedAuthOptions   = Array.isArray(data) ? data : [];
          this.oldServiceOptions = this.getServiceCodeOptions(this.usedAuthOptions);
          if (this.usedAuthOptions.length === 0) {
            this.notificationService.error('Info', 'No used authorizations found for the selected criteria.');
          }
        },
        error: () => {
          this.notificationService.error('Error', 'Failed to load used authorizations.');
        },
      });
  }

  onOldServiceSelected(serviceCode: string | null): void {
    this.adjustmentForm.patchValue({
      oldAuthId:      null,
      newServiceCode: serviceCode ?? '',
      newAuthId:      null,
    });
    this.oldAuthOptions = serviceCode
      ? this.usedAuthOptions.filter(auth => auth.service_code === serviceCode)
      : [];
    this.newAuthOptions = [];
    this.activeAuthOptions = [];

    if (serviceCode) {
      this.loadNewServiceCodes(serviceCode);
      this.loadNewAuths();
    } else {
      this.newServiceOptions = [];
    }
  }

  onOldAuthSelected(authId: number | null): void {
    this.adjustmentForm.get('newAuthId')?.setValue(null);
    this.applyNewAuthFilter(authId);
  }

  onNewServiceSelected(serviceCode: string | null): void {
    this.adjustmentForm.get('newAuthId')?.setValue(null);
    this.newAuthOptions = [];
    this.activeAuthOptions = [];
    if (serviceCode) {
      this.loadNewAuths();
    }
  }

  onAuthCriteriaChange(): void {
    this.adjustmentForm.patchValue({
      oldServiceCode: '',
      newServiceCode: '',
      oldAuthId:      null,
      newAuthId:      null,
    });
    this.oldServiceOptions = [];
    this.newServiceOptions = [];
    this.usedAuthOptions   = [];
    this.activeAuthOptions = [];
    this.oldAuthOptions    = [];
    this.newAuthOptions    = [];
  }

  private loadNewServiceCodes(defaultServiceCode: string): void {
    const f = this.adjustmentForm.getRawValue();
    const url = `${formatUrl(ENDPOINTS.MASS_ADJUSTMENT.ACTIVE_AUTHS, { programCode: this.programCode })}` +
                `?participantId=${encodeURIComponent(String(f.authParticipantId))}` +
                `&fromDate=${this.formatDate(f.authFromDate as Date)}` +
                `&toDate=${this.formatDate(f.authToDate as Date)}`;

    this.isLoadingNewServices = true;
    this.newServiceOptions = [{
      service_code: defaultServiceCode,
      service_description: this.getServiceDescription(defaultServiceCode),
    }];
    this.http.get(url)
      .pipe(finalize(() => { this.isLoadingNewServices = false; }))
      .subscribe({
        next: (res: any) => {
          const data = res?.responsedata ?? res?.data ?? res ?? [];
          const activeAuths: AuthOption[] = Array.isArray(data) ? data : [];
          const defaultOption = this.newServiceOptions[0];
          this.newServiceOptions = this.getServiceCodeOptions(activeAuths);
          if (!this.newServiceOptions.some(option => option.service_code === defaultServiceCode)) {
            this.newServiceOptions.unshift(defaultOption);
          }
        },
        error: () => {
          this.notificationService.error('Error', 'Failed to load active service codes.');
        },
      });
  }

  loadNewAuths(): void {
    const f = this.adjustmentForm.getRawValue();
    if (!f.authParticipantId || !f.authFromDate || !f.authToDate) {
      this.notificationService.error('Error', 'Please enter Participant ID, From Date and To Date first.');
      return;
    }
    if (!f.newServiceCode) {
      this.notificationService.error('Error', 'Please select the New Service Code first.');
      return;
    }
    const url = `${formatUrl(ENDPOINTS.MASS_ADJUSTMENT.ACTIVE_AUTHS, { programCode: this.programCode })}` +
              `?participantId=${encodeURIComponent(String(f.authParticipantId))}` +
              `&fromDate=${this.formatDate(f.authFromDate as Date)}` +
              `&toDate=${this.formatDate(f.authToDate as Date)}` +
              `&serviceCode=${encodeURIComponent(String(f.newServiceCode))}`;

    this.isLoadingNewAuths = true;
    this.newAuthOptions    = [];
    this.http.get(url)
      .pipe(finalize(() => { this.isLoadingNewAuths = false; }))
      .subscribe({
        next: (res: any) => {
          const data = res?.responsedata ?? res?.data ?? res ?? [];
          this.activeAuthOptions = Array.isArray(data) ? data : [];
          this.applyNewAuthFilter(f.oldAuthId);
          if (this.newAuthOptions.length === 0) {
            this.notificationService.error('Info', 'No active authorizations found for the selected criteria.');
          }
        },
        error: () => {
          this.notificationService.error('Error', 'Failed to load active authorizations.');
        },
      });
  }

  private applyNewAuthFilter(oldAuthId: number | null): void {
    this.newAuthOptions = this.activeAuthOptions.filter(
      auth => auth.tas_authorization_detail_id !== oldAuthId
    );
  }

  private getServiceCodeOptions(auths: AuthOption[]): ServiceCodeOption[] {
    const unique = new Map<string, ServiceCodeOption>();
    for (const auth of auths) {
      if (auth.service_code && !unique.has(auth.service_code)) {
        unique.set(auth.service_code, {
          service_code: auth.service_code,
          service_description: auth.service_description || auth.service_code,
        });
      }
    }
    return Array.from(unique.values());
  }

  private getServiceDescription(serviceCode: string): string {
    return this.usedAuthOptions.find(auth => auth.service_code === serviceCode)?.service_description
      || serviceCode;
  }

  getOldAuthLabel(auth: AuthOption): string {
    return this.getAuthLabel(auth);
  }

  getNewAuthLabel(auth: AuthOption): string {
    return this.getAuthLabel(auth);
  }

  private getAuthLabel(auth: AuthOption): string {
    return [auth.ppl_auth_number, auth.plan_servicecode].filter(Boolean).join(' — ');
  }

  // ── File upload handlers ──────────────────────────────────────────────────

  onFileSelected(event: any): void {
    const selectedFile: File | undefined = event?.file;
    if (!selectedFile) return;

    const isDuplicate = this.uploadedFiles.some(
      f => f.name === selectedFile.name && f.size === selectedFile.size,
    );
    if (isDuplicate) return;

    this.uploadedFiles = [...this.uploadedFiles, selectedFile];
    this.inputMode = 'upload';
    this.uploadFile(selectedFile);
  }

  onFilesUploaded(files: any[]): void {
    if (files?.length) {
      this.notificationService.success('Success', `${files.length} file(s) uploaded successfully`);
    }
  }

  onFileRemoved(file: any): void {
    const fileId = typeof file === 'object' ? file?.id : file;

    this.uploadedFiles = this.uploadedFiles.filter(existing => {
      if (fileId !== undefined && (existing as any)?.id !== undefined) {
        return String((existing as any).id) !== String(fileId);
      }
      if (typeof file === 'object') {
        return !(existing.name === file.name && existing.size === file.size);
      }
      return true;
    });

    this.fileList = this.fileList.filter(entry =>
      fileId !== undefined ? String(entry?.id) !== String(fileId) : entry?.name !== file?.name);

    if (this.uploadedFiles.length === 0) {
      this.fileUploadDetailId = null;
      this.inputMode = null;
    }
  }

  onUploadError(error: any): void {
    this.notificationService.error('Error', error?.message || 'File upload failed');
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  onCancel(): void { this.goBack(); }
  goBack(): void   { this.router.navigate([NAVIGATE_BACK_ROUTE]); }

  // ── Save ──────────────────────────────────────────────────────────────────

  onSave(): void {
    if (!this.programCode) {
      this.notificationService.error('Error', 'Program code is not available.');
      return;
    }
    if (this.adjustmentForm.invalid) {
      this.adjustmentForm.markAllAsTouched();
      return;
    }
    if (!this.canSubmit) {
      if (this.isAuthAdjustmentMode) {
        this.notificationService.error('Error',
          'Please fill in Participant ID, dates, service codes and authorization numbers.');
      } else {
        this.notificationService.error('Error', 'Please add at least one criteria or upload a file.');
      }
      return;
    }
    if (this.isUploadingFile) {
      this.notificationService.error('Error', 'Please wait until file upload completes.');
      return;
    }

    this.isSaving = true;
    const payload = this.buildPayload();

    const request$ = this.isEditMode && this.editBatchInstanceId
      ? this.updateBatch(payload)
      : this.createBatch(payload);

    request$
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: () => {
          if (this.isEditMode) {
            this.notificationService.success('Success', 'Bulk adjustment batch updated successfully');
          }
          this.goBack();
        },
        error: (err: unknown) => {
          console.error('Failed to save bulk adjustment:', err);
          this.notificationService.error(
            'Error',
            this.isEditMode
              ? 'Failed to update bulk adjustment batch'
              : 'Failed to create bulk adjustment batch',
          );
        },
      });
  }

  // ── Private: initialisation ───────────────────────────────────────────────

  private initProgramContext(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programCode = selectedProgram?.program_code ?? '';
    this.adjustmentForm.get('programCode')?.setValue(this.programCode);
  }

  private initLookups(): void {
    this.batchTypeOptions        = this.lookupService.getLookupValue(LOOKUP_KEYS.BATCH_TYPE);
    this.batchReasonOptions      = this.lookupService.getLookupValue(LOOKUP_KEYS.BATCH_REASON);
    this.columnOptions           = this.lookupService.getLookupValue(LOOKUP_KEYS.CRITERIA_COLUMNS);
    this.adjustmentColumnOptions = this.lookupService.getLookupValue(LOOKUP_KEYS.ADJUSTMENT_COLUMNS);
    this.processType             = this.lookupService
      .getLookupValue(LOOKUP_KEYS.PROCESS_TYPE)
      .filter(o => o.lkpValueCode === BATCH_PROCESS_INVOICE || o.lkpValueCode === BATCH_PROCESS_TIMESHEET);
  }

  private initEditState(): void {
    const state = this.navigationState;
    if (!state?.['editData'] || !state?.['batchInstanceId']) return;

    this.isEditMode           = true;
    this.editBatchInstanceId  = state['batchInstanceId'];

    const raw  = state['editData'];
    const data = raw.responsedata ?? raw;

    this.populateForm(data);
    this.selectedBatchType = data?.batchTypeLkp ?? '';

    if (data?.batchTypeLkp === BATCH_TYPE_AUTH_ADJUSTMENT) {
      this.populateAuthCriteria(data ?? []);
    }

    const uploadId = data?.fileUploadDetailId ?? data?.file_upload_detail_id;
    this.inputMode = uploadId
      ? 'upload'
      : data?.criteriaList?.length > 0 && data?.batchTypeLkp !== BATCH_TYPE_AUTH_ADJUSTMENT
        ? 'criteria'
        : null;
  }

  // ── Private: form population ──────────────────────────────────────────────

  private populateForm(data: any): void {
    this.adjustmentForm.patchValue({
      batchInstanceId: data.batchInstanceId,
      batchTypeLkp:    data.batchTypeLkp,
      batchProcessLkp: data.batchProcessLkp,
      reasonCodeLkp:   data.reasonCodeLkp,
    });

    if (data?.batchTypeLkp !== BATCH_TYPE_AUTH_ADJUSTMENT) {
      this.populateFormArray(this.criteriaFormArray, data.criteriaList);
    }

    this.fileUploadDetailId = data?.fileUploadDetailId ?? data?.file_upload_detail_id ?? null;

    if (this.fileUploadDetailId != null) {
      const name = data?.actualFileName ?? data?.actual_file_name ?? data?.fileName ?? 'Uploaded file';
      this.fileList = [{ id: this.fileUploadDetailId, name, size: 0, type: 'Bulk Adjustment Files' }];
    } else {
      this.fileList = [];
    }
  }

  private populateFormArray(formArray: FormArray<FormGroup>, items?: any[]): void {
    formArray.clear();
    if (!items?.length) return;
    for (const item of items) {
      formArray.push(this.fb.group({
        columnLkp:   [item.columnLkp   ?? '', Validators.required],
        operatorLkp: [item.operatorLkp ?? '', Validators.required],
        value:       [item.value       ?? '', Validators.required],
      }));
    }
  }

  private populateAuthCriteria(data: any): void {
    const criteriaList = data?.criteriaList ?? [];
    const oldAuth: AuthOption | null = data?.authAdjustmentDetails?.oldAuth ?? null;
    const newAuth: AuthOption | null = data?.authAdjustmentDetails?.newAuth ?? null;

    // nz-select needs the selected records in its option list to display their labels.
    this.oldAuthOptions = oldAuth ? [oldAuth] : [];
    this.newAuthOptions = newAuth ? [newAuth] : [];
    this.usedAuthOptions = oldAuth ? [oldAuth] : [];
    this.activeAuthOptions = newAuth ? [newAuth] : [];

    for (const c of criteriaList) {
      switch (c.columnLkp) {
        case 'participant_id':
          this.adjustmentForm.get('authParticipantId')?.setValue(c.value);
          break;
        case 'from_date':
          this.adjustmentForm.get('authFromDate')?.setValue(c.value ? new Date(c.value) : null);
          break;
        case 'to_date':
          this.adjustmentForm.get('authToDate')?.setValue(c.value ? new Date(c.value) : null);
          break;
        case 'old_service_code':
          this.adjustmentForm.get('oldServiceCode')?.setValue(c.value);
          this.oldServiceOptions = [{
            service_code: c.value,
            service_description: oldAuth?.service_description || c.value,
          }];
          break;
        case 'new_service_code':
          this.adjustmentForm.get('newServiceCode')?.setValue(c.value);
          this.newServiceOptions = [{
            service_code: c.value,
            service_description: newAuth?.service_description || c.value,
          }];
          break;
        case 'old_auth_id': {
          const oldAuthId = oldAuth?.tas_authorization_detail_id ?? Number(c.value);
          this.adjustmentForm.get('oldAuthId')?.setValue(
            Number.isFinite(oldAuthId) ? oldAuthId : null
          );
          break;
        }
        case 'new_auth_id': {
          const newAuthId = newAuth?.tas_authorization_detail_id ?? Number(c.value);
          this.adjustmentForm.get('newAuthId')?.setValue(
            Number.isFinite(newAuthId) ? newAuthId : null
          );
          break;
        }
      }
    }
  }

  // ── Private: row factory ──────────────────────────────────────────────────

  private createRowGroup(): FormGroup {
    return this.fb.group({
      columnLkp:   ['', Validators.required],
      operatorLkp: ['', Validators.required],
      value:       ['', Validators.required],
    });
  }

  // ── Private: payload ──────────────────────────────────────────────────────

  private buildPayload(): BatchPayload {
    if (this.isAuthAdjustmentMode) {
      return this.buildAuthAdjustmentPayload();
    }

    const form    = this.adjustmentForm.getRawValue();
    const mapRows = (rows: any[]): RowItem[] =>
      (rows ?? []).map(r => ({ columnLkp: r.columnLkp, operatorLkp: r.operatorLkp, value: r.value }));

    return {
      batchInstanceId: form.batchInstanceId ?? '',
      batchTypeLkp:    form.batchTypeLkp    ?? '',
      reasonCodeLkp:   form.reasonCodeLkp   ?? '',
      batchProcessLkp: form.batchProcessLkp ?? '',
      programCode:     this.programCode,
      isActive:        !!form.isActive,
      criteriaList:    mapRows(form.criteria),
      fileDetails:     { fileUploadDetailId: this.fileUploadDetailId },
    };
  }

  private buildAuthAdjustmentPayload(): BatchPayload {
    const form = this.adjustmentForm.getRawValue();
    return {
      batchInstanceId: form.batchInstanceId ?? '',
      batchTypeLkp:    BATCH_TYPE_AUTH_ADJUSTMENT,
      reasonCodeLkp:   form.reasonCodeLkp   ?? '',
      batchProcessLkp: form.batchProcessLkp ?? '',
      programCode:     this.programCode,
      isActive:        true,
      criteriaList: [
        { columnLkp: 'participant_id', operatorLkp: 'EQUAL', value: String(form.authParticipantId ?? '') },
        { columnLkp: 'from_date',      operatorLkp: 'EQUAL', value: this.formatDate(form.authFromDate as Date) },
        { columnLkp: 'to_date',        operatorLkp: 'EQUAL', value: this.formatDate(form.authToDate  as Date) },
        { columnLkp: 'old_service_code', operatorLkp: 'EQUAL', value: String(form.oldServiceCode ?? '') },
        { columnLkp: 'new_service_code', operatorLkp: 'EQUAL', value: String(form.newServiceCode ?? '') },
        { columnLkp: 'old_auth_id',    operatorLkp: 'EQUAL', value: String(form.oldAuthId ?? '') },
        { columnLkp: 'new_auth_id',    operatorLkp: 'EQUAL', value: String(form.newAuthId ?? '') },
      ],
      fileDetails: { fileUploadDetailId: null },
    };
  }

  // ── Private: API calls ────────────────────────────────────────────────────

  private createBatch(payload: BatchPayload) {
    return this.timesheetService.saveMassBatch(this.programCode, payload);
  }

  private updateBatch(payload: BatchPayload) {
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.UPDATE_BATCH, {
      programCode:     this.programCode,
      batchInstanceId: this.editBatchInstanceId!,
    });
    return this.http.patch(url, payload);
  }

  // ── Private: file upload ──────────────────────────────────────────────────

  private uploadFile(file: File): void {
    if (!this.programCode) {
      this.notificationService.error('Error', 'Program code is not available for file upload.');
      this.removeLocalFile(file);
      return;
    }

    const url      = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.FILE_UPLOAD, { programCode: this.programCode });
    const formData = new FormData();
    formData.append('file', file, file.name);

    this.isUploadingFile = true;
    this.http
      .post(url, formData as any, true)
      .pipe(finalize(() => { this.isUploadingFile = false; }))
      .subscribe({
        next: (response: any) => {
          const uploadedId = this.extractFileUploadId(response);
          if (uploadedId == null || uploadedId === '') {
            this.removeLocalFile(file);
            this.notificationService.error('Error', 'File uploaded but upload ID was not returned by API.');
            return;
          }
          this.fileUploadDetailId = uploadedId;
          this.uploadedFiles = this.uploadedFiles.map(f =>
            f === file ? Object.assign(f, { id: uploadedId }) : f,
          );

          const data        = response?.responsedata ?? response?.responseData ?? response?.data ?? {};
          const displayName = data?.actual_file_name ?? data?.actualFileName ?? file.name;
          this.fileList = [
            ...this.fileList,
            { id: uploadedId, name: displayName, size: file.size, type: 'Bulk Adjustment Files' },
          ];

          this.notificationService.success('Success', 'File uploaded successfully.');
        },
        error: (error: unknown) => {
          this.removeLocalFile(file);
          const message = error instanceof Error ? error.message : 'File upload failed';
          this.notificationService.error('Error', message);
        },
      });
  }

  private removeLocalFile(file: File): void {
    this.uploadedFiles = this.uploadedFiles.filter(f => f !== file);
  }

  private extractFileUploadId(response: any): number | string | null {
    const data = response?.responsedata ?? response?.responseData ?? response?.data ?? {};
    return (
      response?.fileUploadDetailId ??
      data?.file_upload_detail_id ??
      data?.fileUploadDetailId ??
      response?.fileUploadId ??
      data?.fileUploadId ??
      response?.id ??
      data?.id ??
      data?.fileId ??
      null
    );
  }

  // ── Private: helpers ──────────────────────────────────────────────────────

  private formatDate(date: Date | null): string {
    if (!date) return '';
    const d     = new Date(date);
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day   = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private clearAuthAdjustmentState(): void {
    this.adjustmentForm.patchValue({
      authParticipantId: '',
      authFromDate:      null,
      authToDate:        null,
      oldServiceCode:    '',
      newServiceCode:    '',
      oldAuthId:         null,
      newAuthId:         null,
    });
    this.oldServiceOptions = [];
    this.newServiceOptions = [];
    this.usedAuthOptions   = [];
    this.activeAuthOptions = [];
    this.oldAuthOptions    = [];
    this.newAuthOptions    = [];
  }
}
